import {Component, EventEmitter, Inject} from "@angular/core";
import {AbstractProdForm, ProdFormField, ProdFormFieldType, ProdFormValidators, RequiredValidator} from 'prod-form';
import {IProdMap, MarkerAddressPoint, MarkerDropped, ProdMarker} from 'prod-map';
import {AbstractProdSelectService} from 'prod-select';
import {ProdDialogService, ProdDialogData} from 'prod-dialog';
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {OrganizationTreeSelect} from "../../../../dip/organization-tree-select";
import {HubMapTemplate} from "../../../../dip/map/template/hub-map-template";
import {DevicesService} from "../../../../services/devices.service";
import {RoleService} from "../../../../services/role.service";
import {Role} from "../../../../dip/role";
import {TranslateService} from "@ngx-translate/core";
import {Subscription} from "rxjs";
import {FormEvent} from "prod-grid";

@Component({
  selector: 'hub-form',
  templateUrl: './hub-form.html'
})
export class HubForm extends AbstractProdForm implements IProdMap {
  title: string = this.translate.instant('hub.form.title');
  mapTitle: string = this.translate.instant('hub.form.map_title');
  mapName: string = 'carMap';
  markers$: EventEmitter<Map<number, ProdMarker>> = new EventEmitter<Map<number, ProdMarker>>();
  isFieldsEditable: boolean = this.roleService.hasRole([Role.ROLE_HUB_EDIT]);
  fields: ProdFormField[] = [
    ProdFormField.create({
      name: 'name',
      label: this.translate.instant('hub.form.name'),
      type: ProdFormFieldType.NUMBER,
      validators: new ProdFormValidators([
        new RequiredValidator(),
      ]),
      isEditable: this.isFieldsEditable,
      isMultiEditable: false
    }),
    ProdFormField.create({
      name: 'tag',
      label: this.translate.instant('hub.form.tag'),
      type: ProdFormFieldType.TEXT,
      isEditable: this.isFieldsEditable
    }),
    ProdFormField.create({
      name: 'deviceType',
      label: '',
      type: ProdFormFieldType.HIDDEN,
      value: 3,
      isEditable: false,
    }),
    ProdFormField.create({
      name: 'organization',
      label: '',
      type: ProdFormFieldType.HIDDEN,
      value: this.organizationTreeSelect.site.id,
      isEditable: false,
    }),
    ProdFormField.create({
      name: 'hub_macAddress',
      label: this.translate.instant('hub.form.mac_address'),
      type: ProdFormFieldType.TEXT,
      validators: new ProdFormValidators([
        new RequiredValidator(),
      ]),
      isEditable: this.isFieldsEditable,
      isMultiEditable: false
    }),
    ProdFormField.create({
      name: 'hub_latitude',
      label: this.translate.instant('hub.form.latitude'),
      type: ProdFormFieldType.NUMBER,
      validators: new ProdFormValidators([
        new RequiredValidator(),
      ]),
      value: this.organizationTreeSelect.site.latitude,
      isEditable: this.isFieldsEditable
    }),
    ProdFormField.create({
      name: 'hub_longitude',
      label: this.translate.instant('hub.form.longitude'),
      type: ProdFormFieldType.NUMBER,
      validators: new ProdFormValidators([
        new RequiredValidator(),
      ]),
      value: this.organizationTreeSelect.site.longitude,
      isEditable: this.isFieldsEditable
    }),
    ProdFormField.create({
      name: 'hub_radius',
      label: this.translate.instant('hub.form.radius'),
      type: ProdFormFieldType.NUMBER,
      isEditable: false,
      value: 75,
      disabledOnAdd: true
    }),
    ProdFormField.create({
      name: 'hub_rssiPower',
      label: this.translate.instant('hub.form.rssi_power'),
      type: ProdFormFieldType.NUMBER,
      isEditable: false,
      disabledOnAdd: true
    }),
  ];

  confirmReactivateHubSub: Subscription;

  constructor(dialogRef: MatDialogRef<HubForm>,
              @Inject(MAT_DIALOG_DATA) public dialogData: ProdDialogData,
              override dataService: DevicesService,
              private organizationTreeSelect: OrganizationTreeSelect,
              private roleService: RoleService,
              private prodSelectService: AbstractProdSelectService,
              private dialogService: ProdDialogService,
              private translate: TranslateService) {
    super();
  }

  ngOnInit() {
    this.confirmReactivateHubSub = this.dataService.confirmReactivateHub$.subscribe(value => {
      if (value) {
        this.dataService.confirmReactivateHub$.next(null);
        const request = value.request;

        this.dialogService.confirm(this.translate.instant('hub.form.confirm_reactivate', {hub: request.body.name}))
          .subscribe(confirm => {
            if (confirm === true) {
              let updatedBody = request.body;
              updatedBody['force'] = true;
              if (request.method === 'PATCH') {
                const id = request.url.split('/').pop();
                this.dataService.prodGridPatch(updatedBody, +id).subscribe(res => {
                  if (res['success']) this.data.formResponseEvent.emit(new FormEvent());
                });
              }
              if (request.method == 'POST') {
                this.dataService.prodGridAdd(updatedBody).subscribe(res => {
                  if (res['success']) this.data.formResponseEvent.emit(new FormEvent());
                });
              }
            }
          });
      }
    })
  }

  ngAfterViewInit(): void {
    const markers: Map<number, ProdMarker> = new Map();
    if (this.dialogData.data.records) {
      this.dialogData.data.records.getAll().forEach(hub => {
        markers.set(hub['id'], new ProdMarker({
            id: hub['id'],
            name: hub['name'],
            lat: hub['hub']['latitude'],
            lng: hub['hub']['longitude'],
            icon: HubMapTemplate.getIcon(hub),
            tooltip: HubMapTemplate.getTooltip(hub),
            popup: HubMapTemplate.getPopup(hub),
            pulse: false,
            radius: 0
          }
        ));
      });
    } else {
      markers.set(0, this.getDummyHubMarker());
    }

    this.markers$.emit(markers);
  }

  ngOnDestroy() {
    this.confirmReactivateHubSub.unsubscribe();
  }

  prodMapEvents(event) {
    switch (event.constructor.name) {
      case MarkerDropped.name: {
        const value = <MarkerDropped>event;
        this.prodSelectService.selectValue.next({field: 'hub_latitude', values: value.latLng.lat});
        this.prodSelectService.selectValue.next({field: 'hub_longitude', values: value.latLng.lng});
        break;
      }
      case MarkerAddressPoint.name: {
        const value = <MarkerAddressPoint>event;
        this.prodSelectService.selectValue.next({field: 'address', values: value.address});
      }
    }
  }

  private getDummyHubMarker(): ProdMarker {
    const hub = {name: ''};
    return new ProdMarker({
        id: 0,
        name: '',
        radius: 0,
        pulse: false,
        lng: this.organizationTreeSelect.site.longitude,
        lat: this.organizationTreeSelect.site.latitude,
        icon: HubMapTemplate.getIcon(hub),
        tooltip: '',
        popup: ''
      }
    );
  }
}
